import { useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import ReactFlow, {
  Background, Controls, BackgroundVariant,
  type Node, type Edge,
  Handle, Position, type NodeProps,
} from 'reactflow';
import 'reactflow/dist/style.css';
import type { DossierSectionProps } from './types';
import type { SeedType } from '@/types/profiler';
import { SEED_LABELS } from '@/types/profiler';

// ── colour map ────────────────────────────────────────────────────────────────

const NODE_COLORS: Record<string, string> = {
  CENTER:   '#FF1F1F',
  EMAIL:    '#2979FF',
  PHONE:    '#00C853',
  COMPANY:  '#F0B429',
  PASSPORT: '#FF6B1A',
  RELATION: '#C026D3',
  USERNAME: '#00C8FF',
  ADDRESS:  '#F0B429',
  PAN:      '#00C853',
  VOTER_ID: '#00C853',
  DL:       '#00C853',
  NAME:     '#00C8FF',
  DOMAIN:   '#2979FF',
  IP:       '#FF6B1A',
  SANCTIONS:'#FF1F1F',
};

// ── ReactFlow custom nodes ────────────────────────────────────────────────────

function CenterNode({ data }: NodeProps) {
  const c = '#FF1F1F';
  return (
    <div
      style={{
        width: 88, height: 88, borderRadius: '50%',
        background: '#1A0000',
        border: `2.5px solid ${c}`,
        boxShadow: `0 0 24px ${c}44, inset 0 0 16px ${c}18`,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        cursor: 'default',
        userSelect: 'none',
      }}
    >
      <Handle type="source" position={Position.Bottom} style={{ opacity: 0, pointerEvents: 'none' }} />
      <span style={{ color: c, fontFamily: 'monospace', fontWeight: 900, fontSize: 18, lineHeight: 1 }}>
        {data.initials}
      </span>
      <span style={{ color: '#FF6B1A', fontFamily: 'monospace', fontSize: 7, marginTop: 3, letterSpacing: '0.1em' }}>
        SUBJECT
      </span>
    </div>
  );
}

function TypeNode({ data }: NodeProps) {
  const c: string = data.color ?? '#5A7A9A';
  return (
    <div
      style={{
        minWidth: 76, padding: '8px 14px', borderRadius: 10,
        background: `${c}18`,
        border: `1.5px solid ${c}`,
        boxShadow: `0 0 14px ${c}28`,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0, pointerEvents: 'none' }} />
      <span style={{ color: c, fontFamily: 'monospace', fontWeight: 900, fontSize: 18, lineHeight: 1 }}>
        {data.count}
      </span>
      <span style={{ color: c, fontFamily: 'monospace', fontSize: 8, marginTop: 3, letterSpacing: '0.07em' }}>
        {data.label}
      </span>
    </div>
  );
}

function SanctionsNode({ data }: NodeProps) {
  const c = '#FF1F1F';
  return (
    <div
      style={{
        minWidth: 76, padding: '8px 14px', borderRadius: 10,
        background: `${c}18`,
        border: `1.5px solid ${c}`,
        boxShadow: `0 0 18px ${c}44`,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        userSelect: 'none',
      }}
    >
      <Handle type="target" position={Position.Top} style={{ opacity: 0, pointerEvents: 'none' }} />
      <span style={{ color: c, fontFamily: 'monospace', fontWeight: 900, fontSize: 18, lineHeight: 1 }}>
        {data.count}
      </span>
      <span style={{ color: c, fontFamily: 'monospace', fontSize: 8, marginTop: 3, letterSpacing: '0.07em' }}>
        SANCTIONS
      </span>
    </div>
  );
}

const NODE_TYPES = { center: CenterNode, type: TypeNode, sanctions: SanctionsNode };

// ── build graph data ──────────────────────────────────────────────────────────

function buildGraph(
  canonicalName: string,
  variants: { type: SeedType; value: string; source: string }[],
  sanctionsCount: number,
): { nodes: Node[]; edges: Edge[] } {
  const initials = canonicalName
    .split(/\s+/).slice(0, 2)
    .map(w => w[0]?.toUpperCase() ?? '').join('');

  // group variants by type
  const grouped: Record<string, number> = {};
  for (const v of variants) {
    grouped[v.type] = (grouped[v.type] ?? 0) + 1;
  }

  const satellites: { type: string; count: number; color: string; label: string }[] = [];

  for (const [type, count] of Object.entries(grouped)) {
    satellites.push({
      type,
      count,
      color: NODE_COLORS[type] ?? '#5A7A9A',
      label: SEED_LABELS[type as SeedType] ?? type,
    });
  }

  if (sanctionsCount > 0) {
    satellites.push({ type: 'SANCTIONS', count: sanctionsCount, color: '#FF1F1F', label: 'Sanctions' });
  }

  const CX = 0, CY = 0;
  const RADIUS = satellites.length <= 4 ? 200 : satellites.length <= 6 ? 220 : 240;

  const nodes: Node[] = [
    {
      id: 'center',
      type: 'center',
      position: { x: CX - 44, y: CY - 44 },
      data: { initials, label: canonicalName },
      draggable: false,
    },
  ];

  const edges: Edge[] = [];

  satellites.forEach((sat, i) => {
    const angle = (2 * Math.PI * i) / satellites.length - Math.PI / 2;
    const x = CX + RADIUS * Math.cos(angle);
    const y = CY + RADIUS * Math.sin(angle);
    const id = `sat-${i}`;

    nodes.push({
      id,
      type: sat.type === 'SANCTIONS' ? 'sanctions' : 'type',
      position: { x: x - 38, y: y - 26 },
      data: { ...sat },
    });

    edges.push({
      id: `e-${i}`,
      source: 'center',
      target: id,
      animated: true,
      style: {
        stroke: sat.color,
        strokeWidth: 1.5,
        strokeOpacity: sat.type === 'SANCTIONS' ? 0.9 : 0.55,
      },
    });
  });

  return { nodes, edges };
}

// ── source breakdown ──────────────────────────────────────────────────────────

function SourceBar({ source, count, total, color }: { source: string; count: number; total: number; color: string }) {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
      <span style={{ color, fontFamily: 'monospace', fontSize: 9, minWidth: 120, flexShrink: 0 }}>
        {source}
      </span>
      <div style={{ flex: 1, background: '#0F1928', borderRadius: 4, height: 6, overflow: 'hidden' }}>
        <motion.div
          initial={{ width: 0 }}
          animate={{ width: `${pct}%` }}
          transition={{ duration: 0.8, ease: 'easeOut', delay: 0.2 }}
          style={{ height: 6, borderRadius: 4, background: color, boxShadow: `0 0 5px ${color}66` }}
        />
      </div>
      <span style={{ color, fontFamily: 'monospace', fontWeight: 700, fontSize: 9, minWidth: 20, textAlign: 'right' }}>
        {count}
      </span>
    </div>
  );
}

// ── identifier list ───────────────────────────────────────────────────────────

const SRC_COLORS: Record<string, string> = {
  'breach': '#FF6B1A', 'leakosint': '#FF6B1A', 'osint': '#00C8FF',
  'pan': '#00C853', 'aadhaar': '#00C853', 'mca21': '#F0B429',
  'holehe': '#C026D3', 'default': '#5A7A9A',
};

function srcColor(src: string): string {
  const k = src.toLowerCase();
  for (const [key, col] of Object.entries(SRC_COLORS)) {
    if (k.includes(key)) return col;
  }
  return SRC_COLORS.default;
}

// ── main component ────────────────────────────────────────────────────────────

export function NetworkGraph({ report, pipeline }: DossierSectionProps) {
  const cluster = pipeline?.cluster ?? report.identifier_cluster;
  const variants = (cluster?.variants ?? []) as { type: SeedType; value: string; source: string }[];
  const sanctionsHits = pipeline?.sanctions?.hits ?? [];
  const canonicalName = cluster?.canonical ?? report.seed ?? '—';

  const { nodes, edges } = useMemo(
    () => buildGraph(canonicalName, variants, sanctionsHits.length),
    [canonicalName, variants.length, sanctionsHits.length], // eslint-disable-line react-hooks/exhaustive-deps
  );

  // source breakdown
  const sourceCounts: Record<string, number> = {};
  for (const v of variants) {
    sourceCounts[v.source] = (sourceCounts[v.source] ?? 0) + 1;
  }
  const topSources = Object.entries(sourceCounts).sort((a, b) => b[1] - a[1]).slice(0, 6);
  const maxSrc = topSources[0]?.[1] ?? 1;

  // identifier type breakdown
  const grouped: Record<string, string[]> = {};
  for (const v of variants) {
    (grouped[v.type] = grouped[v.type] ?? []).push(v.value);
  }

  const onInit = useCallback((instance: Parameters<NonNullable<React.ComponentProps<typeof ReactFlow>['onInit']>>[0]) => {
    instance.fitView({ padding: 0.35 });
  }, []);

  const hasData = variants.length > 0 || sanctionsHits.length > 0;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
      style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
    >
      {/* ReactFlow BTRA graph */}
      <div style={{ background: '#090D13', border: '1px solid #182030', borderRadius: 10, overflow: 'hidden' }}>
        <div style={{ padding: '10px 16px', borderBottom: '1px solid #182030', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em' }}>
            BTRA · ENTITY RELATIONSHIP GRAPH
          </span>
          <span style={{ color: '#283A50', fontFamily: 'monospace', fontSize: 8 }}>
            {variants.length} identifiers · {Object.keys(grouped).length} types
          </span>
        </div>
        <div style={{ height: 380 }}>
          {hasData ? (
            <ReactFlow
              nodes={nodes}
              edges={edges}
              nodeTypes={NODE_TYPES}
              onInit={onInit}
              fitView
              fitViewOptions={{ padding: 0.35 }}
              proOptions={{ hideAttribution: true }}
              minZoom={0.4}
              maxZoom={2}
              style={{ background: '#090D13' }}
              nodesDraggable={false}
              nodesConnectable={false}
              elementsSelectable={false}
            >
              <Background
                color="#182030"
                gap={20}
                size={1}
                variant={BackgroundVariant.Dots}
              />
              <Controls
                style={{ background: '#0C1118', border: '1px solid #182030' }}
                showInteractive={false}
              />
            </ReactFlow>
          ) : (
            <div style={{ height: 380, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <p style={{ color: '#283A50', fontFamily: 'monospace', fontSize: 11 }}>
                No identifier network data — run a richer seed type (name, PAN, email)
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
        {[
          { label: 'TOTAL VARIANTS', value: variants.length, color: '#00C8FF' },
          { label: 'UNIQUE TYPES',   value: Object.keys(grouped).length, color: '#2979FF' },
          { label: 'DATA SOURCES',   value: topSources.length, color: '#F0B429' },
        ].map(m => (
          <div key={m.label} style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '12px 14px' }}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              style={{ color: m.color, fontFamily: 'monospace', fontWeight: 800, fontSize: 24 }}
            >
              {m.value}
            </motion.div>
            <div style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 8, marginTop: 4, letterSpacing: '0.08em' }}>
              {m.label}
            </div>
          </div>
        ))}
      </div>

      {/* Identifier type breakdown */}
      {Object.keys(grouped).length > 0 && (
        <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '14px 16px' }}>
          <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', marginBottom: 14 }}>
            IDENTIFIER BREAKDOWN
          </p>
          {Object.entries(grouped).map(([type, values]) => {
            const c = NODE_COLORS[type] ?? '#5A7A9A';
            const pct = variants.length > 0 ? (values.length / variants.length) * 100 : 0;
            return (
              <div key={type} style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                  <span style={{ color: c, fontFamily: 'monospace', fontSize: 9, minWidth: 100, flexShrink: 0 }}>
                    {SEED_LABELS[type as SeedType] ?? type}
                  </span>
                  <div style={{ flex: 1, background: '#0F1928', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${pct}%` }}
                      transition={{ duration: 0.7, ease: 'easeOut', delay: 0.1 }}
                      style={{ height: 8, borderRadius: 4, background: c, boxShadow: `0 0 6px ${c}55` }}
                    />
                  </div>
                  <span style={{ color: c, fontFamily: 'monospace', fontWeight: 700, fontSize: 10, minWidth: 24, textAlign: 'right' }}>
                    {values.length}
                  </span>
                </div>
                {values.slice(0, 4).map((v, i) => (
                  <div key={i} style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    paddingLeft: 110, marginBottom: 2,
                  }}>
                    <div style={{ width: 4, height: 4, borderRadius: '50%', background: `${c}60`, flexShrink: 0 }} />
                    <span style={{ color: '#3A5A7A', fontFamily: 'monospace', fontSize: 9, wordBreak: 'break-all' }}>
                      {v}
                    </span>
                  </div>
                ))}
                {values.length > 4 && (
                  <div style={{ paddingLeft: 110, marginTop: 2 }}>
                    <span style={{ color: '#283A50', fontFamily: 'monospace', fontSize: 8 }}>
                      +{values.length - 4} more
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Source breakdown */}
      {topSources.length > 0 && (
        <div style={{ background: '#0C1118', border: '1px solid #182030', borderRadius: 8, padding: '14px 16px' }}>
          <p style={{ color: '#5A7A9A', fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', marginBottom: 14 }}>
            ENRICHMENT SOURCES
          </p>
          {topSources.map(([source, count]) => (
            <SourceBar key={source} source={source} count={count} total={maxSrc} color={srcColor(source)} />
          ))}
        </div>
      )}

      {/* Sanctions hits preview */}
      {sanctionsHits.length > 0 && (
        <div style={{ background: '#0C1118', border: '1px solid #FF1F1F30', borderLeft: '3px solid #FF1F1F', borderRadius: 8, padding: '14px 16px' }}>
          <p style={{ color: '#FF1F1F', fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', marginBottom: 12 }}>
            SANCTIONS NETWORK HITS
          </p>
          {sanctionsHits.slice(0, 4).map((hit, i) => (
            <div key={hit.entity_id ?? i} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                <span style={{ color: '#DCE8F5', fontFamily: 'monospace', fontSize: 10, fontWeight: 600 }}>
                  {hit.caption}
                </span>
                <span style={{ background: '#FF1F1F18', color: '#FF1F1F', fontFamily: 'monospace', fontSize: 8, fontWeight: 700, padding: '1px 8px', borderRadius: 99, flexShrink: 0 }}>
                  {Math.round(hit.score * 100)}%
                </span>
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                {hit.datasets.slice(0, 4).map(ds => (
                  <span key={ds} style={{ background: '#FF6B1A12', color: '#FF6B1A', fontFamily: 'monospace', fontSize: 8, padding: '1px 6px', borderRadius: 99, border: '1px solid #FF6B1A20' }}>
                    {ds}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
}
